import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { CMSContext } from "../context/CaseManagmentContext";
import { useSelector } from "react-redux";
import AddNewClientModal from "../../../dialogs/AddNewClientDialog";
import AddNewCaseModal from "../../../dialogs/AddNewCaseDialog";
import { updateCaseApi } from "../../../services/api/caseManagementApi";
const InformationCard = ({ title, data, onSave, editable, loading }) => {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const ownerId = userInfo._id;

  const { selectedCase, fetchCases } = useContext(CMSContext);

  const caseAdvocate = selectedCase?.advocates?.find(
    (advocate) => advocate.userId === ownerId
  );

  const isOwner =
    selectedCase?.createdBy === ownerId || caseAdvocate?.role === "Owner";

  const [formData, setFormData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [openCaseModal, setOpenCaseModal] = useState(false);

  useEffect(() => {
    if (Array.isArray(data)) {
      setFormData(data);
    }
    if (data.length === 0) {
      const client = selectedCase?.clientDetails || [];
      setFormData(client);
    }
  }, [data, selectedCase]);

  const handleViewDetails = (client) => {
    setSelectedClient(client);
    setOpenModal(true);
  };

  const handleRemoveClient = (index) => {
    const updatedClients = formData.filter((_, i) => i !== index);
    setFormData(updatedClients);
    const updatedCase = {
      ...selectedCase,
      clients: updatedClients.map((client) => client._id),
    };
    // Remove unwanted keys
    delete updatedCase.lawyerDetails;
    delete updatedCase.clientDetails;
    updateCase(updatedCase); // Pass the updated client list to the parent component
  };

  const handleAddClient = () => {
    setOpenCaseModal(true);
  };

  const updateCase = async (updates) => {
    try {
      const response = await updateCaseApi(selectedCase?._id, updates);

      if (response.status !== 200) {
        throw new Error("Failed to update the case");
      }

      const result = response.data;
      console.log(result.message);
      toast.success("Lawyer removed from the case.");
      fetchCases(true); // Refresh the cases after update
    } catch (error) {
      console.error("Error updating case:", error);
      // Handle error (e.g., show error message)
    }
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        p: 1,
        maxHeight: 390,
        minHeight: 390,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#003D78",
          color: "#FFFFFF",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          p: "4px 8px 4px 8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontSize={"15px"}>{title}</Typography>
        {isOwner && (
          <Tooltip title="Add Client" arrow>
            <Button
              size="small"
              onClick={handleAddClient}
              sx={{
                bgcolor: "#FFFFFF",
                minWidth: "25px",
                minHeight: "25px",
                padding: 0,
                borderRadius: "50%",
                boxShadow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": { bgcolor: "#FFFFFF", cursor: "pointer" },
              }}
            >
              <FontAwesomeIcon
                icon={faUserPlus}
                style={{ fontSize: "14px", color: "#003D78" }}
              />
            </Button>
          </Tooltip>
        )}
      </Box>
      <CardContent
        sx={{
          p: 0,
          overflowY: "auto",
          "::-webkit-scrollbar": { width: "5px" },
          "::-webkit-scrollbar-track": { background: "#fff" },
          "::-webkit-scrollbar-thumb": {
            background: "#bbb",
            borderRadius: "6px",
          },
        }}
      >
        {/* Client Cards Stack */}
        {formData.length === 0 ? (
          <Typography align="center" sx={{ fontSize: "13px", mt: 2 }}>
            The case has no clients.{" "}
          </Typography>
        ) : (
          formData.map((client, index) => (
            <Box
              key={index}
              sx={{
                borderBottom: "1px solid #e0e0e0",
                borderRadius: "4px",
                p: "4px 8px 4px 8px",
                mt: 2,
                backgroundColor: "#f1f8ff",
                boxShadow: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="#003D78"
                gutterBottom
              >
                {client.name || "N/A"}
              </Typography>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography fontSize="13px" fontWeight="bold">
                  Email:
                </Typography>
                <Typography fontSize="13px">{client.email || "N/A"}</Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography fontSize="13px" fontWeight="bold">
                  Profession:
                </Typography>
                <Typography fontSize="13px">
                  {client.profession || "N/A"}
                </Typography>
              </Box>

              {/* Actions */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Tooltip title="View Details">
                  <IconButton
                    onClick={() => handleViewDetails(client)}
                    sx={{
                      color: "#ffffff",
                      backgroundColor: "#003D78",
                      "&:hover": {
                        backgroundColor: "primary.main",
                      },
                      transition: "background-color 0.3s ease-in-out",
                      width: "24px",
                      height: "24px",
                    }}
                  >
                    <VisibilityIcon
                      fontSize="small"
                      sx={{ fontSize: "16px" }}
                    />
                  </IconButton>
                </Tooltip>

                {isOwner && (
                  <Tooltip title="Remove">
                    <IconButton
                      onClick={() => handleRemoveClient(index)}
                      sx={{
                        color: "#ffffff",
                        backgroundColor: "#d32f2f",
                        "&:hover": {
                          backgroundColor: "#b71c1c",
                        },
                        width: "24px",
                        height: "24px",
                      }}
                    >
                      <DeleteIcon fontSize="small" sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))
        )}
      </CardContent>
      {openModal && (
        <AddNewClientModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          data={selectedClient}
          preview={true}
        />
      )}
      {openCaseModal && (
        <AddNewCaseModal
          open={openCaseModal}
          onClose={() => setOpenCaseModal(false)}
          data={selectedCase}
          addNewClient={true}
        />
      )}
    </Card>
  );
};

export default InformationCard;
